"use client";

import { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import Link from "next/link";
import { Loader2, Reply, CornerDownRight } from "lucide-react";
import Image from "next/image";
import ImageUpload from "@/components/common/ImageUpload";
import { formatDate } from "@/lib/utils";

interface Comment {
    id: string;
    content: string;
    createdAt: string;
    author: {
        name: string | null;
        email: string;
    };
    imageUrl: string | null;
    parentId: string | null;
    replies?: Comment[];
}

interface CommentSectionProps {
    inbId: string;
}

export default function CommentSection({ inbId }: CommentSectionProps) {
    const { data: session } = useSession();
    const [comments, setComments] = useState<Comment[]>([]);
    const [newComment, setNewComment] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // State for tracking which comment is being replied to
    const [replyingTo, setReplyingTo] = useState<string | null>(null);
    const [replyContent, setReplyContent] = useState("");

    const fetchComments = useCallback(async () => {
        try {
            const response = await fetch(`/api/comments?inbId=${inbId}`);
            const data = await response.json();

            // Build tree structure from flat list
            const flatComments: Comment[] = data.comments || [];
            const commentMap = new Map<string, Comment>();
            const rootComments: Comment[] = [];

            // First pass: initialize replies array and map
            flatComments.forEach(comment => {
                comment.replies = [];
                commentMap.set(comment.id, comment);
            });

            // Second pass: link parents and children
            flatComments.forEach(comment => {
                if (comment.parentId) {
                    const parent = commentMap.get(comment.parentId);
                    if (parent) {
                        parent.replies?.push(comment);
                    }
                } else {
                    rootComments.push(comment);
                }
            });

            // Sort by date desc (newest first)
            setComments(rootComments);
        } catch (error) {
            console.error("Error fetching comments:", error);
        } finally {
            setIsLoading(false);
        }
    }, [inbId]);

    useEffect(() => {
        fetchComments();
    }, [fetchComments]);

    const handleSubmit = async (e: React.FormEvent, parentId: string | null = null) => {
        e.preventDefault();
        const content = parentId ? replyContent : newComment;

        if ((!content.trim() && !imageUrl) || !session) return;

        setIsSubmitting(true);
        try {
            const response = await fetch("/api/comments", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    inbId,
                    content,
                    imageUrl: parentId ? "" : imageUrl, // Only top level supports images for now
                    parentId,
                }),
            });

            if (response.ok) {
                // Refresh comments to rebuild tree
                await fetchComments();

                if (parentId) {
                    setReplyingTo(null);
                    setReplyContent("");
                } else {
                    setNewComment("");
                    setImageUrl("");
                }
            }
        } catch (error) {
            console.error("Error posting comment:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const CommentItem = ({ comment, depth = 0 }: { comment: Comment, depth?: number }) => (
        <div className={`card p-4 ${depth > 0 ? 'ml-4 md:ml-8 border-l-4 border-l-gray-100 dark:border-l-gray-800' : ''}`}>
            <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-sm font-bold text-white">
                        {(comment.author.name || comment.author.email || "?")[0].toUpperCase()}
                    </div>
                    <div>
                        <p className="font-medium text-sm text-gray-900 dark:text-white">
                            {comment.author.name || comment.author.email}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDate(new Date(comment.createdAt))}
                        </p>
                    </div>
                </div>

                {session && depth < 3 && (
                    <button
                        onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                        className="text-xs flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                    >
                        <Reply size={14} /> Odpowiedz
                    </button>
                )}
            </div>

            <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{comment.content}</p>

            {comment.imageUrl && (
                <div className="mt-3 relative w-full h-48 md:h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                    <Image
                        src={comment.imageUrl}
                        alt="Odebierz załącznik"
                        fill
                        className="object-contain"
                    />
                </div>
            )}

            {/* Reply Input */}
            {replyingTo === comment.id && (
                <form onSubmit={(e) => handleSubmit(e, comment.id)} className="mt-4 pl-4 border-l-2 border-blue-500">
                    <textarea
                        value={replyContent}
                        onChange={(e) => setReplyContent(e.target.value)}
                        className="input min-h-[80px] text-sm mb-2"
                        placeholder={`Odpowiedz użytkownikowi ${comment.author.name || "..."}`}
                        autoFocus
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={() => setReplyingTo(null)}
                            className="btn btn-ghost text-sm py-1"
                        >
                            Anuluj
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting || !replyContent.trim()}
                            className="btn btn-primary text-sm py-1"
                        >
                            {isSubmitting ? "Wysyłanie..." : "Wyślij odpowiedź"}
                        </button>
                    </div>
                </form>
            )}

            {/* Nested Replies */}
            {comment.replies && comment.replies.length > 0 && (
                <div className="mt-4 space-y-4">
                    {comment.replies.map(reply => (
                        <CommentItem key={reply.id} comment={reply} depth={depth + 1} />
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-8">
            {/* Main Comment Form */}
            {session ? (
                <form onSubmit={(e) => handleSubmit(e, null)} className="card p-4">
                    <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Dodaj komentarz</h3>
                    <div className="mb-4">
                        <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            className="input min-h-[100px]"
                            placeholder="Napisz co myślisz..."
                            maxLength={500}
                        />
                        <div className="mt-2">
                            <ImageUpload
                                value={imageUrl}
                                onChange={(url) => setImageUrl(url || "")}
                                label="Dodaj mema/zdjęcie"
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={isSubmitting || (!newComment.trim() && !imageUrl)}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            {isSubmitting && <Loader2 className="animate-spin" size={16} />}
                            Opublikuj
                        </button>
                    </div>
                </form>
            ) : (
                <div className="card p-6 text-center">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Zaloguj się, aby brać udział w dyskusji
                    </p>
                    <Link href="/login" className="btn btn-primary">
                        Zaloguj się
                    </Link>
                </div>
            )}

            {/* Comments Tree */}
            <div className="space-y-4">
                {isLoading ? (
                    <div className="text-center py-8">
                        <div className="animate-spin w-8 h-8 border-2 border-red-500 border-t-transparent rounded-full mx-auto" />
                    </div>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400 italic">
                        Brak komentarzy. Bądź pierwszy!
                    </div>
                ) : (
                    comments.map((comment) => (
                        <CommentItem key={comment.id} comment={comment} />
                    ))
                )}
            </div>
        </div>
    );
}
